import React, { useState } from 'react';
import { FormControl, Link, ResponsiveImage } from '@/components';
import { isColorDark } from '@/utils';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import Download from '@/assets/downloadButton.svg';

const MediaAssetsLibraryLoggedInModule = ({ data }) => {
  const isDark = isColorDark(data.backgroundColour);
  const { t } = useTranslation('common');
  const [selectedHotel, setSelectedHotel] = useState(t('assetLibrary.$allProperty'));

  const brandAssets = data.assets
    .map((group) => ({
      title: group.title,
      assets: group.assets.filter((item) => item.name === 'Brand'),
    }))
    .filter((group) => group.assets.length > 0);

  const propertyAssets = (data.assets || [])
    .map((item) => ({
      ...item,
      assets: (item.assets || []).filter((asset) => asset.name !== 'Brand'),
    }))
    .filter((item) => item.assets.length > 0);

  // Handle the hotel selection change (global change for all groups)
  const handleHotelChange = (event) => {
    setSelectedHotel(event.target.value);
  };

  // Filter assets based on the selected hotel
  const filteredAssets = (group) => {
    if (selectedHotel && selectedHotel !== t('assetLibrary.$allProperty')) {
      return group.assets.filter((asset) => asset.name === selectedHotel);
    }

    return group.assets;
  };

  return (
    <>
      <motion.div layout>
        <div className="asset-group text-left last:mb-0 md:mb-16">
          {brandAssets?.map((assetsItems, assetsItemIndex) => (
            <>
              <p className="title-04 mx-4 mb-4 mt-[60px] md:mx-6 md:mb-7 lg:mx-10 lg:mb-12 rtl:text-right">
                {assetsItems?.title}
              </p>
              <div className="mb-5 hidden justify-between px-4 md:flex md:px-6 lg:px-10">
                <div className="color-from-bg body-03 font-semibold">{t('assetLibrary.$name')}</div>
                <div className="color-from-bg body-03 font-semibold md:mr-[130px] lg:mr-[255px] lg:w-[145px] md:rtl:ml-[250px] md:rtl:mr-0 lg:rtl:ml-[355px] lg:rtl:mr-0">
                  {t('assetLibrary.$size')}
                </div>
              </div>
              {assetsItems.assets.map((asset, assetIndex) => (
                <div
                  key={assetsItemIndex}
                  className={classNames(
                    'flex flex-col justify-between border-y px-4 py-4 md:flex-row md:items-center md:px-6 md:py-5 lg:px-10 lg:py-6',
                    isDark ? 'border-y-silk/25' : 'border-y-charcoal/25',
                    assetIndex === 0 ? 'border-b-0' : 'border-b',
                  )}
                >
                  <div key={assetIndex} className="w-full md:flex md:items-center md:justify-between">
                    <div className="asset-name flex justify-between">
                      <div className="name body-02 mb-3 font-semibold md:mb-0 rtl:text-right">{asset?.asset?.name}</div>
                      <div className="size body-02 md:hidden">{asset?.asset?.size}</div>
                    </div>

                    <div className="detail-wrapper flex flex-col md:flex-row md:items-center md:gap-x-10">
                      <div className="size body-02 hidden md:flex lg:min-w-[145px]">{asset?.asset?.size}</div>
                      {asset?.asset?.url && (
                        <div className="view flex gap-5 lg:gap-10">
                          <a
                            href={asset?.asset?.url}
                            download
                            className={classNames(isDark ? 'dark' : 'light', 'btn secondary downlad-button')}
                          >
                            <Download role="presentation" aria-label="Dowload" className="h-6 w-6" />
                          </a>
                          <Link
                            link={{ href: asset?.asset?.url, text: t('general.$view') }}
                            className={classNames(isDark ? 'dark' : 'light', 'btn secondary')}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </>
          ))}
        </div>

        <div className="asset-group text-left">
          <AnimatePresence>
            {propertyAssets.map((group, groupIndex) => {
              const uniqueAssetNames = Array.from(new Set(group.assets.map((asset) => asset.name)));

              return (
                <div key={groupIndex}>
                  <p className="title-04 mx-4 mb-4 mt-[60px] md:mx-6 md:mb-7 lg:mx-10 lg:mb-8 rtl:text-right">
                    {group.title}
                  </p>
                  {/* Dropdown Filter for All Groups */}
                  <div className="w-[308px] px-4 md:pl-6 lg:pl-10">
                    <FormControl
                      aria-label={t('assetLibrary.$allProperty')}
                      type="select"
                      required
                      name={`assets-${groupIndex}`}
                      value={selectedHotel}
                      placeholder={t('assetLibrary.$select')}
                      options={
                        filteredAssets(group)?.length > 0
                          ? [
                              { label: t('assetLibrary.$allProperty'), value: t('assetLibrary.$allProperty') },
                              ...uniqueAssetNames.map((uniqueName) => ({
                                label: uniqueName,
                                value: uniqueName,
                              })),
                            ]
                          : [{ label: t('assetLibrary.$allProperty'), value: t('assetLibrary.$allProperty') }]
                      }
                      onChange={handleHotelChange}
                      className={classNames(
                        '[&_.content]:text-charcoal [&_.main-button]:!bg-sandstone/0 [&_.pop-over]:z-10 [&_.popover-title]:!font-medium [&_li]:font-medium',
                        isDark
                          ? '[&_.main-button]:!border-silk/50 [&_.popover-title]:!text-silk'
                          : '[&_.main-button]:!border-charcoal/50 [&_.popover-title]:!text-charcoal',
                      )}
                    />
                  </div>

                  <div className="body-03 color-from-bg mx-4 mb-5 mt-9 font-semibold opacity-75 md:mx-6 md:mb-8 lg:mx-10 rtl:text-right">
                    <>
                      {filteredAssets(group)?.length > 0 ? (
                        <div>
                          {t('general.$showingResults', {
                            showing: filteredAssets(group)?.length,
                            results: group?.assets?.length,
                          })}
                        </div>
                      ) : (
                        <div>
                          {t('general.$showingResults', {
                            showing: 0,
                            results: 0,
                          })}
                        </div>
                      )}
                    </>
                  </div>

                  <div className="mb-5 hidden justify-between px-4 md:flex md:px-6 lg:px-10">
                    <div className="color-from-bg body-03 font-semibold md:w-[250px] lg:w-[450px] rtl:text-right">
                      {t('assetLibrary.$name')}
                    </div>
                    <div className="color-from-bg body-03 font-semibold md:w-[320px] lg:w-[320px] rtl:text-right">
                      {t('assetLibrary.$propertyName')}
                    </div>
                    <div className="color-from-bg body-03 min-w-[60px] font-semibold md:mr-[130px] lg:mr-[255px] lg:w-[145px] rtl:text-right md:rtl:ml-[232px] md:rtl:mr-0 lg:rtl:ml-[280px] lg:rtl:mr-0">
                      {t('assetLibrary.$size')}
                    </div>
                  </div>

                  {/* Filtered Assets for Each Group */}
                  {filteredAssets(group)?.length > 0 ? (
                    <>
                      {filteredAssets(group).map((asset, assetIndex) => (
                        <motion.div
                          key={assetIndex}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ type: 'tween', duration: 0.8 }}
                          className={classNames(
                            'flex flex-col border-y px-4 py-4 md:flex-row md:items-center md:px-6 md:py-5 lg:px-10 lg:py-6',
                            isDark ? 'border-y-silk/25' : 'border-y-charcoal/25',
                            assetIndex === 0 ? 'border-b' : 'border-t-0',
                            asset?.asset?.url?.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)
                              ? 'justify-start'
                              : 'justify-between',
                          )}
                        >
                          {asset?.asset?.url?.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i) && (
                            <Link
                              link={{ href: asset?.asset?.url, target: '_blank' }}
                              className="assetimage-wrapper mb-3 aspect-square w-[48px] md:mb-0 ltr:mr-3 rtl:ml-3 [&_svg]:hidden"
                            >
                              <ResponsiveImage
                                key={asset?.asset?.url}
                                image={{ imageUrl: asset?.asset?.url }}
                                widths={{ xs: 96, md: 96, lg: 96 }}
                                heights={{ xs: 96, md: 96, lg: 96 }}
                              />
                            </Link>
                          )}

                          <div
                            className={classNames(
                              'asset-name flex justify-between',
                              asset?.asset?.url?.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)
                                ? 'md:w-[190px] lg:w-[390px]'
                                : 'md:w-[250px] lg:w-[450px]',
                            )}
                          >
                            <div className="name body-02 mb-3 font-semibold md:mb-0 rtl:text-right">
                              {asset?.asset?.name}
                            </div>

                            <div className="size body-02 md:hidden">{asset?.asset?.size}</div>
                          </div>

                          <p
                            className={classNames(
                              'body-02 mb-3 hidden md:mb-0 md:flex md:w-[250px] lg:w-[325px]',
                              asset?.asset?.url?.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i) ? 'mx-auto' : 'mx-0',
                            )}
                          >
                            {asset?.name}
                          </p>

                          <div className="detail-wrapper flex flex-col md:flex-row md:items-center md:gap-x-10">
                            <div className="size body-02 hidden md:flex lg:min-w-[145px]">{asset?.asset?.size}</div>
                            {asset?.asset?.url && (
                              <div className="flex gap-5 lg:gap-10">
                                <a
                                  href={asset?.asset?.url}
                                  download
                                  className={classNames(isDark ? 'dark' : 'light', 'btn secondary downlad-button')}
                                >
                                  <Download role="presentation" aria-label="Dowload" className="h-6 w-6" />
                                </a>
                                <div className="view md:flex">
                                  <Link
                                    link={{ href: asset?.asset?.url, text: t('general.$view'), target: '_blank' }}
                                    className={classNames(isDark ? 'dark' : 'light', 'btn secondary [&_svg]:hidden')}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </>
                  ) : (
                    <div
                      className={classNames(
                        'flex flex-col justify-between border-y px-4 py-4 font-montserrat md:flex-row md:items-center md:px-6 md:py-5 lg:px-10 lg:py-6',
                        isDark ? 'border-y-silk/25' : 'border-y-charcoal/25',
                      )}
                    >
                      {t('assetLibrary.$noData')}
                    </div>
                  )}
                </div>
              );
            })}
          </AnimatePresence>
        </div>
      </motion.div>
    </>
  );
};

export default MediaAssetsLibraryLoggedInModule;
